import { getIdToken } from './auth';

const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

function friendlyDate(value) {
  if (!value) return '';
  const dateOnly = String(value).slice(0, 10);
  const parsed = new Date(`${dateOnly}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return dateOnly;
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(parsed);
}

function friendlyTime(value) {
  if (!value) return '';
  const [hour, minute] = value.split(':').map(Number);
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(2020, 0, 1, hour, minute));
}

function reservation(value) {
  if (!value) return null;
  return { ...value, guest: value.guestName, date: friendlyDate(value.arrivalDate), time: friendlyTime(value.arrivalTime) };
}

function notification(value, property) {
  return {
    id: value.sourceEventId || value.id || `${value.createdAt || ''}:${value.title || ''}`,
    level: value.level || 'info',
    time: value.createdAt ? new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(value.createdAt)) : 'Now',
    createdAt: value.createdAt || null,
    title: value.title,
    source: value.source || 'Hospitable',
    action: value.action || null,
    subjectId: value.subjectId || null,
    propertyId: value.propertyId || property?.id || null,
    property: value.propertyName || property?.name || 'Altus Retreats',
    category: value.category || 'activity',
    senderName: value.senderName || null,
    preview: value.preview || null,
  };
}

function communication(value) {
  const latest = value.latestMessage;
  return {
    ...value,
    arrival: friendlyDate(value.arrivalDate),
    // A host reaction (heart, thumbs up) on the guest's last message, or "mark handled" in the dashboard, counts as handling it.
    senderLabel: latest?.senderRole === 'guest' && !latest.hostReacted && !value.dismissed ? 'Guest last' : value.dismissed ? 'Handled' : latest ? 'Host replied' : value.unavailable ? 'Unavailable' : 'No messages',
    preview: latest?.preview || (value.unavailable ? 'Message history is temporarily unavailable.' : 'No conversation yet.'),
    createdAt: latest?.createdAt || null,
  };
}

export async function loadCalendarPricing(propertyId, startDate, endDate) {
  if (!baseUrl) return [];
  const query = new URLSearchParams({ start_date: startDate, end_date: endDate });
  const response = await fetch(`${baseUrl}/properties/${encodeURIComponent(propertyId)}/availability?${query}`);
  if (!response.ok) throw new Error(`Calendar pricing returned ${response.status}`);
  const payload = await response.json();
  return Array.isArray(payload.calendar?.days) ? payload.calendar.days : [];
}

function operationsView(value, generatedAt) {
  if (!value || value.error) {
    return {
      property: value?.property ? { ...value.property, weather: value.weather || null } : value?.property,
      error: value?.error || 'Property operations are unavailable',
      weather: value?.weather || null,
      occupancy: 0,
      arrivals: [],
      departures: [],
      comingNext: [],
      turnovers: [],
      communications: [],
      bookingPulse: { reservations: 0, bookedNights: 0, hostRevenue: 0, averageDailyRate: 0, currency: 'USD', channelMix: {} },
      alerts: (value?.notifications || []).map((item) => notification(item, value?.property)),
      calendarReservations: [],
      generatedAt,
    };
  }
  return {
    property: { ...value.property, weather: value.weather || null },
    weather: value.weather || null,
    occupancy: value.occupancy?.next7Days || 0,
    arrivals: (value.arrivals || []).map(reservation),
    departures: (value.departures || []).map((item) => ({ ...reservation(item), date: friendlyDate(item.departureDate), time: friendlyTime(item.departureTime) })),
    comingNext: value.comingNext ? [reservation(value.comingNext)] : [],
    turnovers: value.nextTurnover ? [{ ...value.nextTurnover, date: friendlyDate(value.nextTurnover.date), time: friendlyTime(value.nextTurnover.checkoutTime), nextDate: friendlyDate(value.nextTurnover.nextCheckinDate), nextTime: friendlyTime(value.nextTurnover.nextCheckinTime) }] : [],
    communications: (value.guestCommunications || []).map(communication),
    bookingPulse: value.bookingPulse || { reservations: 0, bookedNights: 0, hostRevenue: 0, averageDailyRate: 0, currency: 'USD', channelMix: {} },
    alerts: (value.notifications || []).map((item) => notification(item, value.property)),
    calendarReservations: value.calendar || [],
    orphanNights: value.orphanNights || [],
    capabilities: value.capabilities,
    devices: value.devices || null,
    generatedAt,
  };
}

async function fetchOperations(propertyId) {
  if (!baseUrl) return null;
  const token = await getIdToken();
  const query = propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : '';
  const response = await fetch(`${baseUrl}/dashboard/operations${query}`, { headers: { Authorization: token } });
  if (!response.ok) throw new Error(`Operations API returned ${response.status}`);
  return response.json();
}

export async function loadOperations(propertyId) {
  const payload = await fetchOperations(propertyId);
  if (!payload) return null;
  const value = payload.properties?.[0];
  if (!value || value.error) throw new Error(value?.error || 'Property operations are unavailable');
  return operationsView(value, payload.generatedAt);
}

export async function loadPortfolioOperations() {
  const payload = await fetchOperations();
  if (!payload) return [];
  return (payload.properties || []).map((value) => operationsView(value, payload.generatedAt));
}

export async function dismissGuestMessage(propertyId, reservationId, messageId) {
  if (!baseUrl) return { dismissed: true };
  const token = await getIdToken();
  const response = await fetch(`${baseUrl}/dashboard/messages/dismiss`, {
    method: 'POST',
    headers: { Authorization: token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyId, reservationId, messageId }),
  });
  if (!response.ok) throw new Error(`Mark handled returned ${response.status}`);
  return response.json();
}

export async function loadPortfolioNotifications() {
  if (!baseUrl) return [];
  const token = await getIdToken();
  const response = await fetch(`${baseUrl}/dashboard/notifications`, { headers: { Authorization: token } });
  if (!response.ok) throw new Error(`Notifications API returned ${response.status}`);
  const payload = await response.json();
  return (payload.properties || []).map((value) => ({
    propertyId: value.property?.id,
    alerts: (value.notifications || []).map((item) => notification(item, value.property)),
  })).filter((item) => item.propertyId);
}

async function authorizedGet(path, propertyId) {
  if (!baseUrl) return null;
  const token = await getIdToken();
  const response = await fetch(`${baseUrl}${path}?propertyId=${encodeURIComponent(propertyId)}`, { headers: { Authorization: token } });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || `${path} returned ${response.status}`);
  return payload;
}

// Read-only PriceLabs pricing board data, cached on the server (it changes about once a day).
export async function loadPricing(propertyId) {
  const payload = await authorizedGet('/dashboard/pricing', propertyId);
  return payload?.pricing || null;
}

// Year, quarter and month KPIs derived from Hospitable reservations.
export async function loadKpis(propertyId) {
  const payload = await authorizedGet('/dashboard/kpis', propertyId);
  return payload?.kpis || null;
}

export async function loadDashboardSettings() {
  if (!baseUrl) return { configured: false, links: [] };
  const token = await getIdToken();
  const response = await fetch(`${baseUrl}/dashboard/settings`, { headers: { Authorization: token } });
  if (!response.ok) throw new Error(`Dashboard settings returned ${response.status}`);
  return response.json();
}

export async function saveDashboardSettings(links) {
  if (!baseUrl) return { configured: true, links };
  const token = await getIdToken();
  const response = await fetch(`${baseUrl}/dashboard/settings`, {
    method: 'PUT',
    headers: { Authorization: token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ links }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || `Dashboard settings returned ${response.status}`);
  return payload;
}

export async function uploadQuickLinkLogo(file) {
  if (!baseUrl) return URL.createObjectURL(file);
  const token = await getIdToken();
  const signResponse = await fetch(`${baseUrl}/dashboard/media/sign`, {
    method: 'POST',
    headers: { Authorization: token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, contentType: file.type }),
  });
  const signed = await signResponse.json().catch(() => ({}));
  if (!signResponse.ok) throw new Error(signed.error || `Logo upload could not start (${signResponse.status})`);
  const uploadResponse = await fetch(signed.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  if (!uploadResponse.ok) throw new Error(`Logo upload failed (${uploadResponse.status})`);
  return signed.fileUrl;
}
